import { BaseModel } from "../base.model";
import { AppearanceEnum } from "src/app/_enum/form/appearance.enum";
import { GroupModel } from "./group.model";
import { RuleModel } from "../rule/rule.model";
import { ColumnEnum } from "../../_enum/form/column.enum";
import { StatusEnum } from "../../_enum/form/status.enum";
import { ConfigsModel } from "./configs.model";
import { MiniFormModel } from "./mini-form.model";
import { TriggerEnum } from "../../_enum/rule/trigger.enum";
import { sortRules } from "../../_helper/rule.helper";
import { MatFormFieldAppearance } from "@angular/material/form-field";
import { InputModel } from "./input.model";
import { ResourceEnum } from "src/app/_enum/form/resource.enum";

const DEFAULT_STATUS_FORM: Partial<MiniFormModel> = {
    form: 'status',
    resourceName: 'status',
    sections: [
      <GroupModel>{
        label: 'Status Change',
        mapping: '-1',
        order: -1,
        columns: ColumnEnum.One
      }
    ]
};
const DEFAULT_CREATE_FORM: Partial<MiniFormModel> = {
  form: 'create',
  resourceName: 'create',
  sections: [
    <GroupModel>{
      label: 'Basic Information',
      mapping: '0',
      order: 0,
      columns: ColumnEnum.Two,
    },
    <GroupModel>{
      label: 'Address',
      mapping: '1',
      order: 1,
      columns: ColumnEnum.Two,
    }
  ]
};

/**
 * A class that provides all of the expected shared values of all form elements.
 */
export class FormModel extends BaseModel {

  public id: number = null;
  public description: string = null;
  public appearance: MatFormFieldAppearance = AppearanceEnum.Outline;
  public columns: ColumnEnum = ColumnEnum.Two;
  public rules: RuleModel[] = [];
  public sections: GroupModel[] = [];
  public configs: ConfigsModel = null;
  public createForm: MiniFormModel = null;
  public statusForm: MiniFormModel = null;
  public version: number = null;
  public formStatus: StatusEnum = StatusEnum.Draft;
  public customerName: string = 'form';
  public resourceName: ResourceEnum | string = 'property';

  constructor(model?: Partial<FormModel>) {
    super();
    this.overwrite(model);
    this.cleanRules();
    sortRules(this);
  }

  public overwrite(model: Partial<FormModel>, ...exclude: string[]) {
    super.overwrite(model, 'rules', 'sections', 'configs', 'statusForm', 'createForm', ...exclude);
    if (model?.rules?.length) this.rules = model.rules.map(rule => new RuleModel(rule));
    if (model?.sections?.length) this.sections = model.sections.map(section => new GroupModel(section));
    this.configs = new ConfigsModel(model?.configs);
    this.statusForm = new MiniFormModel(
      model?.statusForm
        ? model.statusForm
        : DEFAULT_STATUS_FORM
    );
    this.statusForm.resourceName = 'status';
    this.createForm = new MiniFormModel(
      model?.createForm
        ? model.createForm
        : model?.configs?.['create']
          ? this.generateCreateFormFromCreateConfig(model.configs['create'])
          : DEFAULT_CREATE_FORM
    );
    this.createForm.resourceName = 'create';
  }

  private cleanRules() {
    //make sure certain triggers' tValues are null
    //there was a bug at one point that would populate them with a 0
    for (let rule of this.rules) {
      if ([ TriggerEnum.Init, TriggerEnum.Changes, TriggerEnum.Exists, TriggerEnum.NotExists ].includes(rule.trigger)) {
        rule.tValue = null;
      }
    }
  }

  /**
   * Creates a mini-form based on the create config
   */
  private generateCreateFormFromCreateConfig(oldConfig: any) {

    let fieldOrder = [
      'ListOfficeKey',
      'ListAgentKey',
      'TransactionType',
      'PropertyType',
      'PropertySubType',
      'StreetNumber',
      'StreetName',
      'StreetSuffix',
      'StreetDirSuffix',
      'UnitNumber',
      'CountyOrParish',
      'City',
      'CityRegion',
      'PostalCode',
      'StateOrProvince',
      'Country',
      'CompSaleYN'
    ];

    let form =  {
      form: 'create',
      appearance: this.appearance,
      sections: [
        new GroupModel({
          label: 'Basic Information',
          mapping: '0',
          order: 0,
          columns: ColumnEnum.Two,
          elements: Object.keys(oldConfig.basic || {})
                      .filter(k => fieldOrder.includes(k))
                      .sort((a,b) => fieldOrder.indexOf(a) > fieldOrder.indexOf(b) ? 1 : -1)
                      .map(key =>
                        new InputModel(Object.assign(oldConfig.basic[key], {
                          width: ['ListOfficeKey','ListAgentKey','TransactionType']
                                  .includes(oldConfig.basic[key].mapping) ?
                                    ColumnEnum.Two : ColumnEnum.One
                      })))
        }),
        new GroupModel({
          label: 'Address',
          mapping: '1',
          order: 1,
          columns: ColumnEnum.Two,
          elements: [
            ...Object.keys(oldConfig.address || {})
                .filter(k => fieldOrder.includes(k))
                .sort((a,b) => fieldOrder.indexOf(a) > fieldOrder.indexOf(b) ? 1 : -1)
                .map(key => new InputModel(oldConfig.address[key])),
            ...Object.keys(oldConfig.other || {})
                .filter(k => fieldOrder.includes(k))
                .sort((a,b) => fieldOrder.indexOf(a) > fieldOrder.indexOf(b) ? 1 : -1)
                .map(key => new InputModel(oldConfig.other[key]))
          ]
        })
      ],
      rules: [
        // MAYBE: do we want to pull rules from the main form?
      ]
    };
    
    return form;
  }
}
